import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Avatar, Divider, CircularProgress,
  TextField, Button, Snackbar, Alert, IconButton,
  InputAdornment, Switch, List, ListItemButton,
  ListItemIcon, ListItemText, Paper, Dialog,
  DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import LogoutIcon from "@mui/icons-material/Logout";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

const API_BASE = "http://localhost:8000";

function getInitials(name = "") {
  return name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase() || "U";
}

const SCREEN = { MAIN: "main", PROFILE: "profile", PASSWORD: "password" };

function SubHeader({ title, onBack }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
      <IconButton onClick={onBack} size="small" sx={{ backgroundColor: "#f3f4f6", "&:hover": { backgroundColor: "#e5e7eb" } }}>
        <ArrowBackIosNewIcon sx={{ fontSize: 16, color: "#4b5563" }} />
      </IconButton>
      <Typography variant="h6" fontWeight={700} color="#111827">
        {title}
      </Typography>
    </Box>
  );
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const authHeaders = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [screen, setScreen] = useState(SCREEN.MAIN);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifEnabled, setNotifEnabled] = useState(true);

  const [formName, setFormName] = useState("");
  const [saving, setSaving] = useState(false);

  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const showSnack = (message, severity = "success") => setSnack({ open: true, message, severity });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/user/profile`, { headers: authHeaders });
        const json = await res.json();
        if (json.success) {
          setUser(json.data);
          setFormName(json.data.name || "");
        }
      } catch (e) { console.error(e); } 
      finally { setLoading(false); }
    })();
  }, []);

  const handleSaveProfile = async () => {
    if (!formName.trim()) return showSnack("Nama tidak boleh kosong", "error");
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/user/profile`, {
        method: "PUT", headers: authHeaders, body: JSON.stringify({ name: formName.trim() }),
      });
      const json = await res.json();
      if (json.success) {
        setUser((prev) => ({ ...prev, name: formName.trim() }));
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        localStorage.setItem("user", JSON.stringify({ ...stored, name: formName.trim() }));
        setScreen(SCREEN.MAIN);
        showSnack("Profil berhasil diperbarui");
      } else {
        showSnack(json.message || "Gagal memperbarui profil", "error");
      }
    } catch { showSnack("Terjadi kesalahan", "error"); } 
    finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (!oldPw || !newPw || !confirmPw) return showSnack("Semua field wajib diisi", "error");
    if (newPw !== confirmPw) return showSnack("Konfirmasi password tidak cocok", "error");
    if (newPw.length < 8) return showSnack("Password minimal 8 karakter", "error");
    setPwSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/user/change-password`, {
        method: "POST", headers: authHeaders,
        body: JSON.stringify({ old_password: oldPw, new_password: newPw, new_password_confirmation: confirmPw }),
      });
      const json = await res.json();
      if (json.success) {
        setOldPw(""); setNewPw(""); setConfirmPw("");
        setScreen(SCREEN.MAIN);
        showSnack("Password berhasil diubah");
      } else {
        showSnack(json.message || "Gagal mengubah password", "error");
      }
    } catch { showSnack("Terjadi kesalahan", "error"); } 
    finally { setPwSaving(false); }
  };

  const confirmLogout = async () => {
    setLoggingOut(true);
    try {
      await fetch(`${API_BASE}/api/logout`, { method: "POST", headers: authHeaders });
    } catch (e) { console.error("Logout error:", e); } 
    finally {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setLoggingOut(false);
      setLogoutDialogOpen(false);
      navigate("/login");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  const initials = getInitials(user?.name);
  const roleName = user?.role?.name || user?.role || "User";

  const pwFields = [
    { label: "Password Lama", value: oldPw, setter: setOldPw, show: showOld, toggle: () => setShowOld((p) => !p) },
    { label: "Password Baru", value: newPw, setter: setNewPw, show: showNew, toggle: () => setShowNew((p) => !p) },
    { label: "Konfirmasi Password Baru", value: confirmPw, setter: setConfirmPw, show: showConfirm, toggle: () => setShowConfirm((p) => !p) },
  ];

  const SnackbarEl = (
    <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
      <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} variant="filled" sx={{ borderRadius: "8px", fontWeight: 500 }}>
        {snack.message}
      </Alert>
    </Snackbar>
  );

  // ── SCREEN: Change Password ──
  if (screen === SCREEN.PASSWORD) {
    return (
      <Box sx={{ maxWidth: 500, mx: "auto" }}>
        <SubHeader title="Ubah Password" onBack={() => { setScreen(SCREEN.MAIN); setOldPw(""); setNewPw(""); setConfirmPw(""); }} />
        <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid #e5e7eb", p: 3, backgroundColor: "#fff" }}>
          <Typography sx={{ fontSize: "0.85rem", color: "#6b7280", mb: 2 }}>
            Pastikan password Anda minimal 8 karakter dan mengandung kombinasi yang aman.
          </Typography>
          {pwFields.map(({ label, value, setter, show, toggle }) => (
            <TextField
              key={label} label={label} fullWidth type={show ? "text" : "password"}
              value={value} onChange={(e) => setter(e.target.value)}
              size="medium" margin="normal"
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ fontSize: 20, color: "#9ca3af" }} /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={toggle} edge="end">
                        {show ? <VisibilityOffOutlinedIcon fontSize="small" sx={{ color: "#9ca3af" }} /> : <VisibilityOutlinedIcon fontSize="small" sx={{ color: "#9ca3af" }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />
          ))}
          <Button fullWidth variant="contained" size="large" startIcon={pwSaving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
            onClick={handleChangePassword} disabled={pwSaving} disableElevation
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, py: 1.2, mt: 3, backgroundColor: "#1677ff", "&:hover": { backgroundColor: "#0958d9" } }}>
            {pwSaving ? "Menyimpan..." : "Simpan Password"}
          </Button>
        </Paper>
        {SnackbarEl}
      </Box>
    );
  }

  // ── SCREEN: Edit Profile ──
  if (screen === SCREEN.PROFILE) {
    return (
      <Box sx={{ maxWidth: 500, mx: "auto" }}>
        <SubHeader title="User Profile" onBack={() => { setScreen(SCREEN.MAIN); setFormName(user?.name || ""); }} />
        <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden", backgroundColor: "#fff", mb: 3 }}>
          <Box sx={{ p: 4, display: "flex", flexDirection: "column", alignItems: "center", backgroundColor: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
            <Avatar sx={{ width: 80, height: 80, fontSize: "1.8rem", fontWeight: 800, background: "linear-gradient(135deg, #1677ff 0%, #7c3aed 100%)", boxShadow: "0 4px 14px rgba(22,119,255,0.25)", mb: 2 }}>
              {initials}
            </Avatar>
            <Typography variant="h6" fontWeight={700} color="#111827">{user?.name || "User"}</Typography>
            <Typography variant="body2" color="#6b7280">{roleName}</Typography>
          </Box>
          <Box sx={{ px: 3, py: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>Email</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5, mb: 2.5 }}>
              <EmailOutlinedIcon sx={{ fontSize: 20, color: "#9ca3af" }} />
              <Typography variant="body2" fontWeight={600} color="#374151">{user?.email || "—"}</Typography>
            </Box>
            <Divider sx={{ borderColor: "#f3f4f6", mb: 2.5 }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>Nama Lengkap</Typography>
            <TextField fullWidth value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Masukkan nama lengkap" size="medium" sx={{ mt: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fafafa" } }}
              slotProps={{ input: { startAdornment: <InputAdornment position="start"><PersonOutlineIcon sx={{ fontSize: 20, color: "#9ca3af" }} /></InputAdornment> } }}
            />
          </Box>
        </Paper>
        <Button fullWidth variant="contained" size="large" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          onClick={handleSaveProfile} disabled={saving} disableElevation
          sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, py: 1.2, backgroundColor: "#1677ff", "&:hover": { backgroundColor: "#0958d9" } }}>
          {saving ? "Menyimpan..." : "Simpan Perubahan"}
        </Button>
        {SnackbarEl}
      </Box>
    );
  }

  // ── SCREEN: Main Settings ──
  return (
    <Box sx={{ maxWidth: 500, mx: "auto" }}>
      {/* Header Profile Card */}
      <Paper elevation={0} sx={{ display: "flex", alignItems: "center", gap: 2.5, p: 2.5, borderRadius: "12px", border: "1px solid #e5e7eb", backgroundColor: "#fff", mb: 3 }}>
        <Avatar sx={{ width: 60, height: 60, fontSize: "1.4rem", fontWeight: 800, background: "linear-gradient(135deg, #1677ff 0%, #7c3aed 100%)", boxShadow: "0 4px 14px rgba(22,119,255,0.25)" }}>
          {initials}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} color="#111827" noWrap>{user?.name || "User"}</Typography>
          <Typography variant="body2" color="#6b7280" noWrap>{user?.email || "—"}</Typography>
        </Box>
      </Paper>

      <Typography sx={{ px: 1, mb: 1, fontSize: "0.75rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        Pengaturan Akun
      </Typography>

      {/* Menu list */}
      <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden", backgroundColor: "#fff", mb: 3 }}>
        <List disablePadding>
          <ListItemButton onClick={() => setScreen(SCREEN.PROFILE)} sx={{ px: 2.5, py: 1.8 }}>
            <ListItemIcon sx={{ minWidth: 44 }}><Box sx={{ width: 36, height: 36, borderRadius: "8px", backgroundColor: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center" }}><PersonOutlineIcon sx={{ fontSize: 20, color: "#2563eb" }} /></Box></ListItemIcon>
            <ListItemText primary="User Profile" slotProps={{ primary: { fontWeight: 600, fontSize: "0.95rem", color: "#374151" } }} />
            <ChevronRightIcon sx={{ fontSize: 20, color: "#d1d5db" }} />
          </ListItemButton>
          <Divider component="li" sx={{ ml: 9, borderColor: "#f3f4f6" }} />
          <ListItemButton onClick={() => setScreen(SCREEN.PASSWORD)} sx={{ px: 2.5, py: 1.8 }}>
            <ListItemIcon sx={{ minWidth: 44 }}><Box sx={{ width: 36, height: 36, borderRadius: "8px", backgroundColor: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}><LockOutlinedIcon sx={{ fontSize: 20, color: "#dc2626" }} /></Box></ListItemIcon>
            <ListItemText primary="Ubah Password" slotProps={{ primary: { fontWeight: 600, fontSize: "0.95rem", color: "#374151" } }} />
            <ChevronRightIcon sx={{ fontSize: 20, color: "#d1d5db" }} />
          </ListItemButton>
          <Divider component="li" sx={{ ml: 9, borderColor: "#f3f4f6" }} />
          <Box sx={{ display: "flex", alignItems: "center", px: 2.5, py: 1.8 }}>
            <Box sx={{ minWidth: 44 }}><Box sx={{ width: 36, height: 36, borderRadius: "8px", backgroundColor: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center" }}><NotificationsNoneIcon sx={{ fontSize: 20, color: "#16a34a" }} /></Box></Box>
            <Typography sx={{ flexGrow: 1, fontWeight: 600, fontSize: "0.95rem", color: "#374151" }}>Push Notifikasi</Typography>
            <Switch checked={notifEnabled} onChange={(e) => setNotifEnabled(e.target.checked)} color="primary" size="small" />
          </Box>
        </List>
      </Paper>

      <Typography sx={{ px: 1, mb: 1, fontSize: "0.75rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        Lainnya
      </Typography>

      <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden", backgroundColor: "#fff", mb: 3 }}>
        <List disablePadding>
          <ListItemButton component="a" href="https://wa.me/" target="_blank" sx={{ px: 2.5, py: 1.8 }}>
            <ListItemIcon sx={{ minWidth: 44 }}><Box sx={{ width: 36, height: 36, borderRadius: "8px", backgroundColor: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center" }}><WhatsAppIcon sx={{ fontSize: 20, color: "#16a34a" }} /></Box></ListItemIcon>
            <ListItemText primary="Hubungi Dukungan (WhatsApp)" slotProps={{ primary: { fontWeight: 600, fontSize: "0.95rem", color: "#374151" } }} />
          </ListItemButton>
          <Divider component="li" sx={{ ml: 9, borderColor: "#f3f4f6" }} />
          <ListItemButton onClick={() => setLogoutDialogOpen(true)} sx={{ px: 2.5, py: 1.8, "&:hover": { backgroundColor: "#fef2f2" } }}>
            <ListItemIcon sx={{ minWidth: 44 }}><Box sx={{ width: 36, height: 36, borderRadius: "8px", backgroundColor: "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}><LogoutIcon sx={{ fontSize: 20, color: "#ef4444" }} /></Box></ListItemIcon>
            <ListItemText primary="Keluar" slotProps={{ primary: { fontWeight: 600, fontSize: "0.95rem", color: "#ef4444" } }} />
          </ListItemButton>
        </List>
      </Paper>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)} PaperProps={{ sx: { borderRadius: "12px", minWidth: 320 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#111827" }}>Konfirmasi Logout</DialogTitle>
        <DialogContent><Typography sx={{ fontSize: "0.9rem", color: "#4b5563" }}>Apakah Anda yakin ingin keluar dari sistem?</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLogoutDialogOpen(false)} sx={{ textTransform: "none", color: "#6b7280", fontWeight: 600 }}>Batal</Button>
          <Button onClick={confirmLogout} variant="contained" color="error" disabled={loggingOut} sx={{ textTransform: "none", fontWeight: 600, borderRadius: "6px" }}>
            {loggingOut ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Ya, Logout"}
          </Button>
        </DialogActions>
      </Dialog>

      {SnackbarEl}
    </Box>
  );
}